// Edge fades for a horizontal scroller: stamps `data-fade-start` /
// `data-fade-end` on the element while a child is CUT by that edge, so CSS
// can mask the cut into a soft gradient instead of a hard line (see
// `.meter-grid` in index.css). Once scroll-snap settles a card flush with
// the edge nothing is cut and that side stays crisp, even mid-list. Written
// straight to the DOM from the scroll listener — no React state, no
// re-render per scroll frame.

import { useCallback } from 'react';

export function useEdgeFade<T extends HTMLElement>(): (el: T | null) => (() => void) | undefined {
  return useCallback((el: T | null) => {
    if (!el) return undefined;
    const update = () => {
      const box = el.getBoundingClientRect();
      let start = false;
      let end = false;
      // 1px slack: fractional positions on zoomed/HiDPI screens
      for (const child of el.children) {
        const r = child.getBoundingClientRect();
        if (r.left < box.left - 1 && r.right > box.left + 1) start = true;
        if (r.left < box.right - 1 && r.right > box.right + 1) end = true;
      }
      el.toggleAttribute('data-fade-start', start);
      el.toggleAttribute('data-fade-end', end);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    // viewport/container resizes move the edges…
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // …and cards coming or going move the children
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true });
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
}
